import { useApplicantIdReportController } from "../controller";
import { checkIsSurveyManualPending } from "../utils/checkIsSurveyManualPending";
import { Dispatch, SetStateAction, useMemo } from "react";
import { ApplicantCardSlice } from "../slices/ApplicantCardSlice";
import { ReportsCard } from "./ReportsCard";
import { Sidebar } from "views/applications-id-report/components/Sidebar";
import { ApplicantStatus, Verification, VerificationNames } from "shared/types";
import { cx } from "shared/utils";
import { Invitee } from "shared/types";
import { Applicant } from "types/application";
import logoSquare from "shared/branding/logo-square.svg";
import { VerificationSchema } from "types/landlord/verificationTemplate";
import { useAuthContext } from "providers/Auth";
import { FEATURES } from "types/landlord/partner";

export const ApplicationReportLayout = ({
  applicant,
  currentVerification,
  verifications,
  verificationSchemas,
  setShowCancelModal,
  setShowUncancelModal,
  setShowRefundModal,
  setChangeRoleModalApplicant,
  setSetPrimaryModalApplicant,
  setResendInviteModalInvite,
  requiresOverridesIdentityVerification
}: {
  applicant: Applicant;
  currentVerification?: Verification;
  verifications: (Verification | Verification[])[];
  verificationSchemas: {
    [Key in VerificationNames]: VerificationSchema;
  };
  setShowCancelModal: Dispatch<SetStateAction<boolean>>;
  setShowUncancelModal: Dispatch<SetStateAction<boolean>>;
  setShowRefundModal: Dispatch<SetStateAction<boolean>>;
  setChangeRoleModalApplicant: Dispatch<SetStateAction<Applicant | undefined>>;
  setSetPrimaryModalApplicant: Dispatch<SetStateAction<Applicant | undefined>>;
  setResendInviteModalInvite: Dispatch<SetStateAction<Invitee | undefined>>;
  requiresOverridesIdentityVerification: boolean;
}) => {
  const { applicationMeta, searchParams, setSearchParams } =
    useApplicantIdReportController();
  const { partner } = useAuthContext();
  const premiumRentalVerificationEnabled =
    partner?.features.includes(FEATURES.WIP_FEAT_PREMIUM_RENTAL_VERIFICATION) ??
    false;

  const pendingMagicLink = useMemo(() => {
    return applicationMeta.data?.pending_magic_links?.find(
      (link) => String(link.id) === String(searchParams.applicantId)
    );
  }, [searchParams.applicantId, applicationMeta.data]);

  const surveyVerification = (applicant.verifications.find(
    (v) => v.name === VerificationNames.PremiumRentalHistory
  ) ??
    applicant.verifications.find(
      (v) => v.name === VerificationNames.RentalVerification
    )) as Verification | undefined;

  const housingHistoryVerification = applicant.verifications.find(
    (v) => v.name === VerificationNames.HousingHistory
  ) as Verification | undefined;

  const isHousingHistorySurveyManualPending =
    premiumRentalVerificationEnabled &&
    checkIsSurveyManualPending({
      applicantStatus: applicant.application_status as ApplicantStatus,
      surveyVerification,
      housingHistoryVerification
    });

  const isChatView = searchParams.report === "chat";

  const getHeightClass = () => {
    if (isChatView) return "";

    return "min-h-full";
  };

  if (!applicationMeta?.data) return null;

  return (
    <div
      className={cx(
        getHeightClass(),
        "mx-auto flex max-w-[1200px] flex-col gap-4 print:gap-0 print:bg-white"
      )}
    >
      <ApplicantCardSlice
        applicant={applicant}
        applicationStatus={applicationMeta.data.status}
        templateId={applicationMeta.data.verification_template?.id}
        allowCoSigner={
          applicationMeta.data.verification_template!.settings.co_signer
            .enabled_screening_services ?? false
        }
        allowOccupant={Boolean(
          applicationMeta.data.verification_template!.settings.occupant
            ?.enabled_screening_services ?? false
        )}
        pendingMagicLink={pendingMagicLink}
        onCancelClick={() => setShowCancelModal(true)}
        onUncancelClick={() => setShowUncancelModal(true)}
        onChangeRoleClick={(applicant) =>
          setChangeRoleModalApplicant(applicant)
        }
        onSetPrimaryClick={(applicant) =>
          setSetPrimaryModalApplicant(applicant)
        }
        onRefundClick={() => setShowRefundModal(true)}
        onResendInviteClick={(invite) => setResendInviteModalInvite(invite)}
        verificationIds={verifications.flat().reduce((acc, curr) => {
          if (
            [
              VerificationNames.IncomeVerification,
              VerificationNames.HousingHistory
            ].includes(curr.name)
          )
            acc.push(curr.id);

          return acc;
        }, [] as number[])}
      />
      <div
        className={cx(
          !isChatView ? "h-full" : "",
          "flex flex-col rounded-lg border border-alpha-100 bg-white shadow-xs print:w-full print:border-none lg:flex-row print:shadow-none"
        )}
      >
        <Sidebar
          isInvitee={!Boolean(applicant.application_started_at)}
          application={applicationMeta.data}
          applicantId={Number(searchParams.applicantId)}
          currentTab={
            (searchParams.report as VerificationNames) ||
            VerificationNames.RentalApplication
          }
          tabSteps={verifications}
          setCurrentTab={(t) => {
            setSearchParams({ ...searchParams, report: t });
          }}
          requiresOverridesIdentityVerification={
            requiresOverridesIdentityVerification
          }
          isHousingHistorySurveyManualPending={
            isHousingHistorySurveyManualPending
          }
        />
        <div
          className={cx(
            !isChatView ? "min-h-full p-6" : "",
            "w-full print:overflow-y-visible print:border-none print:p-0 lg:border-l lg:border-alpha-40"
          )}
        >
          <ReportsCard
            applicant={applicant}
            currentTab={
              // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
              (searchParams.report as VerificationNames) ||
              VerificationNames.RentalApplication
            }
            requiresOverridesIdentityVerification={
              requiresOverridesIdentityVerification
            }
            applicationMeta={applicationMeta}
            currentVerification={currentVerification}
            screeningCriteria={
              // @ts-expect-error
              applicationMeta.data.verification_template.screening_criteria
                ?.applicant_settings
            }
            verificationSchemas={verificationSchemas}
          />
        </div>
      </div>

      <br />

      <div
        id="printable_documents_root"
        className="hidden print:block bg-white"
      />

      {/* Printable header/footer styles */}
      <div className="fixed top-0 hidden h-1.5 w-full justify-end px-6 print:flex">
        <img
          alt="Boom Logo"
          className="relative top-1 h-[30px] w-[30px]"
          src={logoSquare}
        />
      </div>
    </div>
  );
};
